import { css, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'

const GQL_QUERY_CAMPAIGN = gql`
    query Campaigns {
        campaigns {
            name
            offers {
                name
                sku
                price
                image_url
            }
        }
    }
`

const GQL_MUTATION_CHECKOUT = gql`
    mutation Checkout($checkout: CheckoutInput!) {
        checkout(checkout: $checkout) {
            success
            message
            redirect_url
        }
    }
`

type Offer = {
    sku: string
    qty: number
    name: string
    image: string
    price: number
}

type Currency = {
    symbol: string
    position: 'start' | 'end'
}

const currencyConfig: Currency = {
    symbol: process.env.REACT_APP_CURRENCY_SYMBOL || '$',
    position: 'start'
}

const Container = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 20px;
  margin: 20px;
  padding-bottom: 60px;

  @media (min-width: 524px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`

const Card = styled.div`
  border: 1px solid #ccc;
  padding: 10px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: scale(1.02);
  }
`

const pulse = keyframes`
  0% { background-color: #eee; }
  50% { background-color: #ddd; }
  100% { background-color: #eee; }
`

const LoadingCard = styled.div`
  border: 1px solid #ccc;
  padding: 10px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  & > div {
    background-color: #eee;
    animation: ${pulse} 1s infinite ease-in-out;
  }
`

const OfferImage = styled.img`
  width: 100%;
  height: 210px;
  padding-bottom: 5%;
  object-fit: contain;
`

const OfferName = styled.h2`
  font-size: 18px;
  color: #333;
  margin-top: auto;
`

const OfferPrice = styled.h3`
  font-size: 16px;
  color: #4b0082;
  margin: 10px 0;
`

const OfferActionContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 10px;
`

const QtyWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 34px;
`

const IncrementButton = styled.button`
  background-color: #575757;
  padding: 4px;
  height: 100%;
  width: 35px;
  cursor: pointer;
  border: none;
  color: #ffffff;
  outline: none;

  &:hover {
    background-color: #3a3a3a;
  }
`

const DecrementButton = styled(IncrementButton)``

const QuantityInput = styled.input`
  border: 1px solid #575757;
  padding: 0;
  width: 50px;
  height: 100%;
  box-sizing: border-box;
  margin: 0;
  text-align: center;
  background-color: #fff;
  line-height: 100%;

  &:focus {
    outline: none;
  }
  &:hover {
    outline: none;
  }
`

const Button = styled.button`
  color: #fff;
  background-color: #575757;
  padding: 10px;
  border: none;
  cursor: pointer;
  transition: background-color 0.3s ease;
  flex-grow: 1;
  margin: 0 0 0 10px;

  &:hover {
    background-color: #3a3a3a;
  }
`

const NavBar = styled.nav`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  padding: 10px 0;
  background-color: #f5f5f5;
  display: flex;
  justify-content: center;
`

const CartItemsCount = styled.span`
  margin-left: 10px;
  font-size: 18px;
  color: #333;
`

const CheckoutButton = styled(Button)`
  flex-grow: 0;
  width: calc(100% - 20px);
  height: 45px;
  text-align: center;
  box-sizing: border-box;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const CheckoutLoader = styled.div`
  border: 4px solid #f3f3f3;
  border-radius: 50%;
  border-top: 4px solid #3498db;
  width: 24px;
  height: 24px;
  animation: spin 2s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const formatPrice = (price: number): string => {
    if (currencyConfig.position === 'start') {
        return `${currencyConfig.symbol}${price.toFixed(2)}`
    } else {
        return `${price.toFixed(2)}${currencyConfig.symbol}`
    }
}

const OfferListing: React.FC = () => {
    const { loading, error, data } = useQuery(GQL_QUERY_CAMPAIGN)
    const [offers, setOffers] = useState<Offer[]>([])

    useEffect(() => {
        if (!loading && !error) {
            const parsedOffers = data?.campaigns[0]?.offers.map((offer: any) => ({
                ...offer,
                image: offer.image_url,
                qty: 0
            }));

            setOffers(parsedOffers ?? [])
        }
    }, [loading, error, data])

    const [offerQtys, setOfferQtys] = useState<Record<string, number>>({})

    const incrementQty = (sku: string) => {
        setOfferQtys((qtys) => {
            const newQtys = { ...qtys, [sku]: (qtys[sku] || 0) + 1 }
            setOffers(currentOffers => currentOffers.map(item => item.sku === sku ? {...item, qty: newQtys[sku]} : item))
            return newQtys
        })
    }

    const decrementQty = (sku: string) => {
        setOfferQtys((qtys) => {
            const newQtys = { ...qtys, [sku]: qtys[sku] > 0 ? qtys[sku] - 1 : 0 }
            setOffers(currentOffers => currentOffers.map(item => item.sku === sku ? {...item, qty: newQtys[sku]} : item))
            return newQtys
        })
    }

    const calculateItemsCount = (): number => {
        return offers.map(p => p.qty).reduce((a, b) => a + b, 0)
    }

    const calculateTotalPrice = (): number => {
        return offers.reduce((total, offer) => total + (offer.qty * offer.price), 0)
    }

    const [checkout, { data: mutationData, loading: mutationLoading, error: mutationError }] = useMutation(GQL_MUTATION_CHECKOUT)

    const doCheckout = () => {
        const checkoutInput = {
            campaign_uuid: process.env.REACT_APP_CAMPAIGN_UUID,
            offers: offers
                .filter(offer => offer.qty > 0)
                .map(({ sku, qty }) => ({ sku, qty })),
        }
        checkout({ variables: { checkout: checkoutInput } })
            .then((response) => {
                if (response.data.checkout.success) {
                    const redirectUrl = response.data.checkout.redirect_url;

                    if (!redirectUrl) {
                        console.error('Could not retrieve redirect URL')
                    } else {
                        window.open(redirectUrl, '_blank')
                    }
                } else {
                    console.error('An error occurred while adding to cart', response.data.checkout.message)
                }
            })
            .catch(error => {
                console.error('An error occurred while adding to cart', error)
            })
    };

    const renderLoading = () => {
        let loadingCards = [];
        for(let i = 0; i < 4; i++) {
            loadingCards.push(
                <LoadingCard key={`loading-card-${i}`}>
                    <div style={{height: '200px', width: '100%', marginBottom: '10px'}}></div>
                    <div style={{height: '20px', width: '60%', marginBottom: '10px'}}></div>
                    <div style={{height: '20px', width: '40%'}}></div>
                </LoadingCard>
            );
        }
        return loadingCards
    }

    if (loading) {
        return (
            <Container>
                {renderLoading()}
            </Container>
        )
    };

    if (error || !data) return <p>Error :(</p>

    return (
        <>
            <Container>
                {offers.map((offer) => (
                    <Card key={offer.sku}>
                        <OfferImage src={offer.image} alt={offer.name} />
                        <OfferName>{offer.name}</OfferName>
                        <OfferPrice>{formatPrice(offer.price)}</OfferPrice>
                        <OfferActionContainer>
                            <QtyWrapper>
                                <DecrementButton onClick={() => decrementQty(offer.sku)}>-</DecrementButton>
                                <QuantityInput type="text" readOnly value={offer.qty} />
                                <IncrementButton onClick={() => incrementQty(offer.sku)}>+</IncrementButton>
                            </QtyWrapper>
                        </OfferActionContainer>
                    </Card>
                ))}
            </Container>
            <NavBar>
                <CheckoutButton onClick={doCheckout} disabled={mutationLoading || calculateItemsCount() === 0}>
                    {mutationLoading ? <CheckoutLoader /> : `Checkout (${calculateItemsCount()} items, ${formatPrice(calculateTotalPrice())} total)`}
                </CheckoutButton>
            </NavBar>
        </>
    )
}

export default OfferListing
